import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { FieldArray, Form, Formik } from 'formik';
import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';

import TextFieldFormik from '../../../common/text_field';
import { SelectFieldFormik, SelectFieldMultipleFormikWithMore } from '../../../common/select_field';
import { getAllPodsState } from '../../../../features/pod/get_all_pods';
import { SubmitButton } from '../../../common/button';
import RadioGroupFormik from '../../../common/radio_group';
import { createNotificationAction, createNotificationState } from '../../../../features/communication/create_notification';
import useNotification from '../../../../core/hooks/use_notification';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import { createNotificationNotificationData } from '../../../../core/utils/notification_utils';
import { getLanguagesState } from '../../../../features/product/get_languages';
import { getNotificationTypesState } from '../../../../features/communication/get_notification_types';
import { MISC_DATE_ISO_FORMAT, ROUTE_NOTIFICATIONS } from '../../../../core/constants';
import { DateTimePickerFormik } from '../../../common/date_picker';
import { parseDateString } from '../../../../core/utils/date_utils';
import { yesNoOptions } from '../../../common/form_utils';
import SectionPaper from '../../../common/section_paper';
import BottomActionBar from "../../../common/bottom_action_bar";

const NotificationForm = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const getAllPods = useSelector(getAllPodsState);
    const createNotification = useSelector(createNotificationState);
    const { showNotification } = useNotification();

    const getLanguages = useSelector(getLanguagesState);
    const getNotificationTypes = useSelector(getNotificationTypesState);

    const initialValues = {
        notificationType: location?.state?.notificationRequest?.notificationType || 'GENERAL',
        requestedRealization: '',
        translatedMessages: getLanguages?.data?.map(language => ({
            languageCode: language.languageCode,
            title: location?.state?.notificationRequest?.translatedMessages?.find(n => n.languageCode === language.languageCode)?.title || '',
            body: location?.state?.notificationRequest?.translatedMessages?.find(n => n.languageCode === language.languageCode)?.body || ''
        })),
        podIds: location?.state?.notificationRequest?.podIds || [],
        sendToAll: !(location?.state?.notificationRequest?.podIds?.length > 0)
    };

    const schema = Yup.object().shape({
        notificationType: Yup.string().required(),
        requestedRealization: Yup.date()
            .transform(parseDateString)
            .nullable()
            .notRequired(),
        translatedMessages: Yup.array().of(
            Yup.object().shape({
                languageCode: Yup.string().required(),
                title: Yup.string().required(),
                body: Yup.string().required(),
            }, ['title', 'body'])
        ),
        sendToAll: Yup.boolean(),
        podIds: Yup.array().when('sendToAll', {
            is: false,
            then: Yup.array().min(1, 'Wybierz przynajmniej jeden punkt z listy'),
            otherwise: Yup.array().notRequired()
        }),
    });

    const onSubmit = (values) => {
        const form = {
            notificationType: values.notificationType,
            requestedRealization: values.requestedRealization ? values?.requestedRealization?.format(MISC_DATE_ISO_FORMAT) : null,
            translatedMessages: values.translatedMessages,
            podIds: values.podIds
        };

        dispatch(createNotificationAction(form))
            .then(response => {
                showNotification(createNotificationNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    navigate(ROUTE_NOTIFICATIONS);
                }
            });
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }
        >
            { (formik) => (
                <Box component={ Form }
                     sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <Stack spacing={ 3 }>

                            <SectionPaper title="Ustawienia wysyłki">
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 } sm={ 6 }>
                                        <SelectFieldFormik
                                            name="notificationType"
                                            label="Typ notyfikacji"
                                            options={ getNotificationTypes?.data?.map(type => ({
                                                value: type,
                                                label: type
                                            })) }
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } sm={ 6 }>
                                        <DateTimePickerFormik
                                            name="requestedRealization"
                                            label="Data wysyłki (pozostaw puste dla natychmiastowej)"
                                            InputLabelProps={ { shrink: true } }
                                        />
                                    </Grid>

                                    <Grid item xs={ 12 }>
                                        <Divider sx={ { my: 1 } }/>
                                    </Grid>

                                    <Grid item xs={ 12 }>
                                        <RadioGroupFormik
                                            name="sendToAll"
                                            label="Wyślij do wszystkich urządzeń"
                                            options={ yesNoOptions }
                                            onChange={ (value) => value === true && formik.setFieldValue('podIds', []) }
                                        />
                                    </Grid>

                                    { !formik.values.sendToAll && (
                                        <Grid item xs={ 12 }>
                                            <SelectFieldMultipleFormikWithMore
                                                name="podIds"
                                                label="Wybierz punkty sprzedaży"
                                                defaultOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId != null).map(pod => ({
                                                    value: pod?.id,
                                                    label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - ${ pod?.device?.serviceId }`
                                                })) }
                                                extraOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId == null).map(pod => ({
                                                    value: pod?.id,
                                                    label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - brak urządzenia`
                                                })) }
                                            />
                                        </Grid>
                                    ) }
                                </Grid>
                            </SectionPaper>

                            <SectionPaper title="Treść wiadomości">
                                <FieldArray name="translatedMessages">
                                    { () => (
                                        getLanguages?.data?.map((language, index) => (
                                            <Box key={ index }
                                                 sx={ { mb: index !== getLanguages.data.length - 1 ? 4 : 0 } }>
                                                <Typography variant="subtitle1" color="primary"
                                                            sx={ { fontWeight: 600, mb: 2 } }>
                                                    { language.languageName }
                                                </Typography>

                                                <Grid container spacing={ 2 }>
                                                    <Grid item xs={ 12 }>
                                                        <TextFieldFormik
                                                            name={ `translatedMessages.${ index }.title` }
                                                            label="Tytuł"
                                                            required
                                                        />
                                                    </Grid>
                                                    <Grid item xs={ 12 }>
                                                        <TextFieldFormik
                                                            name={ `translatedMessages.${ index }.body` }
                                                            label="Treść"
                                                            multiline
                                                            rows={ 4 }
                                                            required
                                                        />
                                                    </Grid>
                                                </Grid>

                                                { index !== getLanguages.data.length - 1 && (
                                                    <Divider sx={ { mt: 4 } }/>
                                                ) }
                                            </Box>
                                        ))
                                    ) }
                                </FieldArray>
                            </SectionPaper>

                        </Stack>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ { display: 'flex', justifyContent: 'flex-end', width: '100%' } }>
                            <SubmitButton isLoading={ createNotification?.loading }>
                                Wyślij powiadomienie
                            </SubmitButton>
                        </Box>
                    </BottomActionBar>

                </Box>
            ) }
        </Formik>
    );
};

export default NotificationForm;